import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Grid, Card, Stack, IconButton, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import timezones from 'timezones-list';
import FormProvider, { RHFSelect, RHFTextField } from '../../../components/hook-form';
import WorkingHoursWithCopy from '../../../components/working-hours/WorkingHoursWithCopy';
import { getValidConfig } from '../../../utils/schedule';
import { currentTimezoneCode } from '../../../utils/timezoneCodes';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Iconify from 'src/components/iconify';
import palette from 'src/theme/palette';
import dayjs from 'dayjs';
import RHFTimezoneSelect from 'src/components/hook-form/RHFTimezoneSelect';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';

const BOOKING_STATUS_OPTIONS = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'No-Show', value: 'NO_SHOW' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Canceled', value: 'CANCELED' },
];

const BUFFER_OPTIONS = [
  { label: 'No Buffer', value: 0 },
  { label: '5 Mins', value: 5 },
  { label: '10 Mins', value: 10 },
  { label: '15 Mins', value: 15 },
  { label: '20 Mins', value: 20 },
  { label: '30 Mins', value: 30 },
  { label: '45 Mins', value: 45 },
  { label: '60 Mins', value: 60 },
];

const StyledLabel = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 2.5,
}));

export default function SettingsForm({ config, updateUser }) {
  const [availability, setAvailability] = useState([]);
  const defaultValues = useMemo(
    () => {
      const validConfig = getValidConfig(config, { shouldIncludeTimezone: true });
      // A timezone saved from an older picker list is carried under its current code,
      // so the next save stores the code the list offers today.
      return {
        config: { ...validConfig, timezone: currentTimezoneCode(validConfig.timezone) },
      };
    },
    [config]
  );

  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    if (defaultValues.config && defaultValues.config.availability) {
      setAvailability(defaultValues.config.availability);
    }
    if (defaultValues.config && defaultValues.config.holidays) {
      const savedHolidays = defaultValues.config.holidays.map((holiday) => {
        return dayjs(holiday);
      });
      setHolidays(savedHolidays);
    }
  }, [defaultValues]);

  const methods = useForm({ defaultValues });
  const {
    handleSubmit,
    setError,
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
  } = methods;

  // Unsaved changes hook - triggered when user navigates away from page
  const { hasUnsavedChanges } = useUnsavedChanges({
    formState: { isDirty, isSubmitSuccessful },
    onUnsavedChanges: null, // No modal on this page, just browser warning
    isOpen: true
  });

  const onSubmit = async (data) => {
    data.config.availability = availability.filter(
      (day) => day.startDate !== 'off' && day.endDate !== 'off'
    );
    data.config.holidays = holidays.filter((day) => day !== null);

    // The page shows the server's message. This only records that the save failed.
    //
    // Caught here rather than left to propagate: react-hook-form 7.43 does not wrap
    // this callback, so a throw skips its closing state update and leaves the Save
    // button spinning. A root error instead makes it report the submit as unsuccessful,
    // which keeps the unsaved-changes warning on for changes that were never saved.
    try {
      await updateUser(data);
    } catch {
      setError('root.server', { type: 'server' });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Card sx={{ p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <WorkingHoursWithCopy
              availability={availability}
              setAvailability={setAvailability}
            />


          </Grid>
          <Grid item xs={12} md={4}>
            {/* <Stack sx={{ mb: 2 }}>
              <StyledLabel>Webflow Site ID:</StyledLabel>
              <RHFTextField name="config[][webflowSiteId]" size="large" />
            </Stack> */}

            <Stack sx={{ mb: 2 }}>
              <StyledLabel>Default Booking Status:</StyledLabel>
              <RHFSelect native name="config[][bookingStatus]" size="large">
                {BOOKING_STATUS_OPTIONS.map((status, index) => (
                  <option key={index} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </RHFSelect>
            </Stack>
            <Stack sx={{ mb: 2 }}>
              <StyledLabel>Buffer Time Before Booking:</StyledLabel>
              <RHFSelect native name="config[][beforeBuffer]" size="large">
                {BUFFER_OPTIONS.map((buffer, index) => (
                  <option key={index} value={buffer.value}>
                    {buffer.label}
                  </option>
                ))}
              </RHFSelect>
            </Stack>
            <Stack>
              <StyledLabel>Buffer Time After Booking:</StyledLabel>
              <RHFSelect native name="config[][afterBuffer]" size="large">
                {BUFFER_OPTIONS.map((buffer, index) => (
                  <option key={index} value={buffer.value}>
                    {buffer.label}
                  </option>
                ))}
              </RHFSelect>
            </Stack>
            <Stack>
              <StyledLabel>Time zone:</StyledLabel>
              {/* <RHFSelect native name="config[][timezone]" size="large">
                {timezones.map(({ tzCode, label }, index) => (
                  <option key={index} value={tzCode}>
                    {label}
                  </option>
                ))}
              </RHFSelect> */}
          <RHFTimezoneSelect
            name="config[][timezone]"
            // label="Timezone"
            size="large"
            timezones={timezones}
            placeholder="Select your timezone"
          />
          

            </Stack>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              flexGrow={1}
              mt={2}
              mb={2}
            >
              <LoadingButton
                type="submit"
                size="medium"
                variant="contained"
                loading={isSubmitting}
              >
                Save Changes
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Card>
    </FormProvider>
  );
}
